package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.box
import com.siriusxm.pia.components.button
import com.siriusxm.pia.components.simpleTextField
import com.siriusxm.pia.components.spinner
import com.siriusxm.transcripts.VttViewer
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Test page for the VTT transcript viewer component.
 * Allows entering a URL and loading VTT files dynamically.
 */
@Composable
fun transcriptTest() {
    var urlInput by remember { mutableStateOf("https://d2fgd2l0cl9ot.cloudfront.net/external-transcriptions/84c11839-0155-426e-a0e2-cf0f6e6b8372/active.vtt") }
    var vttContent by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    val loadVtt = {
        scope.launch {
            if (urlInput.isNotBlank()) {
                isLoading = true
                error = null
                vttContent = null
                try {
                    val response = HttpClient().get(urlInput)
                    vttContent = response.bodyAsText()
                } catch (e: Exception) {
                    error = e.message ?: "Failed to load transcript"
                } finally {
                    isLoading = false
                }
            }
        }
    }

    box("Transcript Viewer Test") {
        Div {
            P({
                style {
                    marginBottom(16.px)
                }
            }) {
                Text("Enter a VTT transcript URL and click Load to display it.")
            }

            // URL input and load button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    marginBottom(24.px)
                    property("align-items", "center")
                }
            }) {
                Div({
                    style {
                        flex(1)
                    }
                }) {
                    simpleTextField(urlInput) { urlInput = it ?: "" }
                }

                button("Load", primary = true) {
                    loadVtt()
                }
            }

            // Display states
            when {
                isLoading -> {
                    Div({
                        style {
                            padding(32.px)
                            textAlign("center")
                        }
                    }) {
                        spinner()
                        P({
                            style {
                                marginTop(8.px)
                                color(SXMUI.subtleTextColor.value())
                            }
                        }) {
                            Text("Loading transcript...")
                        }
                    }
                }
                error != null -> {
                    Div({
                        style {
                            padding(16.px)
                            backgroundColor(SXMUI.messageAlertBackground.value())
                            border(1.px, LineStyle.Solid, SXMUI.messageAlertBorder.value())
                            borderRadius(4.px)
                            color(SXMUI.messageAlertForeground.value())
                        }
                    }) {
                        Text("Error: $error")
                    }
                }
                vttContent != null -> {
                    H2({
                        style {
                            marginBottom(12.px)
                        }
                    }) {
                        Text("Transcript")
                    }

                    Div({
                        style {
                            width(100.percent)
                        }
                    }) {
                        VttViewer(vttContent!!, showViewToggle = true)
                    }
                }
            }
        }
    }
}
